/**
 * RunSession (RFC 0217: reify the run).
 *
 * The callable run surface the harness adapters (web/sandbox/cli) switch to
 * behind a flag during the cutover. It composes the pieces: a WorkerRuntime
 * draining one branch-log with the inference + tool workers (RFC 0216) wired to
 * the real seams (ProxyInferenceClient / RegistryToolExecutor, RFC 0217), and
 * `projectRunStream` so callers keep consuming the legacy StreamChunk stream.
 *
 * Session = branch, relaxed (RFC 0147): a RunSession holds ONE branch-log and
 * can start MANY runs on it (no per-run branch minting). "Session" is this thin
 * driver/label over the branch, not a structure that owns a branch. Branches
 * stay the isolation/merge/access unit elsewhere.
 *
 * Nothing in the product path constructs this yet; the adapter swap is the
 * flagged cutover step (see RFC 0217 §4).
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import type { BranchLog } from '../../log/branch-log'
import type {
  AttachmentRef,
  EntryId,
  MessageSelection,
  MessageView,
  NewEntry,
  RunDispatch,
  ToolWorkerId,
} from '../../log/types'
import { InferenceWorker, ToolWorker, WorkerRuntime } from '../../workers'
import type { RunAuthorization } from '../../workers/run-authorization'
import type { InferenceClient, ToolExecutor } from '../../workers/seams'
import { projectRunStream } from './project-run-stream'
import type { StreamChunk } from './stream-types'

export interface RunSessionDeps {
  inference: InferenceClient
  tools: ToolExecutor
  /** This session's local tool execution-context identity (its device id). */
  toolWorkerId?: ToolWorkerId
  /**
   * RFC 0240: the device this session's runs are addressed to (the responder).
   * Defaults to undefined (unaddressed: any connected worker may claim, the
   * pre-0240 single-device race). Set per-run on startRun/queueRun or via
   * setResponder for a sticky default.
   */
  responder?: ToolWorkerId | null
  /** Agent label on legacy runs; agent key id on protected session runs. */
  agent?: string
  /** RFC 0345 S6: access to a project is not possession of this agent's key.
   * Protected sessions refuse work unless their host supplies this gate. */
  authorizeRun?: RunAuthorization
  /**
   * RFC 0345 S7: a session whose agent is a key of its own is hosted in a repo
   * that signs as that key, not in the host's. Resolved when the session is
   * attached (`hostSessionWorkers`). Undefined hosts it in the host's repo,
   * where a run naming a key is never claimed.
   */
  keyedSession?: () => Promise<
    | {
        repo: Repo
        agent: string
        /** False when the key's repo cannot read the session (a Keyhive key
         * that joined late misses the history before it; known issue 4 in
         * "Sharing: remaining work"). The host then opens the log in its own
         * repo and keeps the key's claim gate. */
        readable?: boolean
      }
    | undefined
  >
  /** The agent key this session's repo signs as, set from `keyedSession`. A
   *  run naming any other key is never claimed here (RFC 0345 3f). */
  agentKey?: string
  /**
   * The World's present device ids (RFC 0261 slice 2), for the inference claim
   * gate: a run placed on another LIVE device makes this worker stand down.
   * Injected by the host (which owns the presence doc); omit for the classic
   * single-device path.
   */
  presentDevices?: () => Iterable<ToolWorkerId>
  /**
   * Whether this host may claim a new run now. A host about to restart closes
   * it, so a run queued meanwhile waits for the next host instead of being cut
   * off mid-turn (RFC 0337 3i). Runs already claimed are not affected.
   */
  mayClaim?: () => boolean
}

export interface StartedRun {
  runId: EntryId
  stream: AsyncIterable<StreamChunk>
}

export class RunSession {
  private readonly runtime: WorkerRuntime
  private started = false
  /** This session's local device id (claims runs addressed to it, or unaddressed). */
  private readonly deviceId: ToolWorkerId
  /** Sticky responder for runs started here (RFC 0240); null = unaddressed. */
  private responder: ToolWorkerId | null

  constructor(
    private readonly log: BranchLog,
    private readonly deps: RunSessionDeps,
  ) {
    this.runtime = new WorkerRuntime(log)
    this.deviceId = deps.toolWorkerId ?? 'tw-local'
    this.responder = deps.responder ?? null
    // RFC 0240: the workers carry this device's identity, so they claim only
    // runs addressed to this device (or unaddressed). Inference follows the tool
    // device (one device choice; inference resolves the device's default model).
    this.runtime.register(
      new InferenceWorker(
        deps.inference,
        this.deviceId,
        deps.presentDevices,
        deps.authorizeRun,
        deps.agentKey,
        deps.mayClaim,
      ),
    )
    this.runtime.register(
      new ToolWorker(
        deps.tools,
        this.deviceId,
        this.deviceId,
        deps.authorizeRun,
      ),
    )
  }

  /**
   * RFC 0240 handoff: address subsequent runs to another device. The session
   * keeps driving the log (it still syncs), but new runs are claimed by the
   * named device (e.g. hand a session from the browser to "Laptop", or to the
   * cloud). Applies to runs started after this call; pass `null` to unaddress.
   */
  setResponder(deviceId: ToolWorkerId | null): void {
    this.responder = deviceId
  }

  /**
   * Append a user prompt + a queued run to the log and return the run id plus
   * its projected legacy stream. Many runs may be started on one branch-log.
   *
   * Robust to timing: projectRunStream re-projects the whole run on each drain,
   * so a caller that starts consuming after the run already advanced (or even
   * completed) still receives the full chunk sequence.
   */
  startRun(prompt: string, responder = this.responder): StartedRun {
    const msgId = this.log.append(
      {
        type: 'message',
        payload: {
          role: 'user',
          status: 'completed',
          text: prompt,
          toolUses: [],
        },
      },
      this.log.heads(),
    )
    return this.queueRun(msgId, responder)
  }

  /**
   * Enqueue + drive a run for an ALREADY-appended user message (RFC 0238).
   *
   * The interactive CLI appends the user message to the log synchronously on
   * send — so it renders immediately and durably, decoupled from the (slow)
   * RunSession build — then calls this to queue the run without re-appending the
   * prompt (which `startRun` would do, duplicating the turn). Same drive/stream
   * semantics as `startRun`.
   */
  queueRun(promptMessageId: EntryId, responder = this.responder): StartedRun {
    // Placement (RFC 0261 slice 3): "address to a device" now means SELECTING it
    // for the thread via worker_migration (whiffletree `selected_tool_worker`),
    // not a per-run field. Skip if already selected, so a sticky responder does
    // not append a redundant migration per turn. Parented to the migration so the
    // run has it in its causal history (`activeToolWorker` walks ancestors).
    let parent = promptMessageId
    if (responder && this.log.activeToolWorker(promptMessageId) !== responder) {
      parent = this.log.migrateWorker(responder, {
        fromWorkerId: this.log.activeToolWorker(promptMessageId) ?? undefined,
      })
    }
    const runId = this.log.append(
      {
        type: 'run',
        payload: {
          promptMessageId,
          agent: this.deps.agent ?? 'default',
          status: 'queued',
        },
      },
      [parent],
    )
    this.ensureStarted()
    return { runId, stream: projectRunStream(this.log, runId) }
  }

  /**
   * RFC 0242: append a user prompt + a queued run to the log WITHOUT starting a
   * local runtime. The run sits `queued` for a REMOTE responder to drain (a
   * background sandbox running `mod session serve`, or any other watcher). The
   * caller renders the reply from the synced log — it does not run inference
   * here. Returns the run id and the projected stream (which fills in as the
   * remote responder writes to the synced log).
   *
   * This is the "hand off to a remote worker" half of startRun: same canonical
   * message + run entries, minus `ensureStarted()`. Address the run to a specific
   * device via `responder` (RFC 0240) or leave it unaddressed (any watcher claims).
   */
  enqueueRemote(prompt: string, responder = this.responder): StartedRun {
    return enqueueRemoteRun(this.log, prompt, {
      agent: this.deps.agent,
      responder,
    })
  }

  /**
   * Attach to the branch-log and drain runs WITHOUT enqueuing one (RFC 0216
   * drain mode). Starts the worker runtime so any run queued on this log — by
   * this peer, the web app, or a sibling session — is driven to completion.
   * Idempotent; pair with `stop()`.
   *
   * This is the headless `worker --project` surface: a process that services
   * runs others enqueue, rather than driving an interactive one via `startRun`.
   */
  watch(): void {
    this.ensureStarted()
  }

  /**
   * Ask workers to re-scan for claimable runs (see `WorkerRuntime.poke`).
   * No-op until `watch()` / `startRun` has started the runtime.
   */
  poke(): void {
    if (!this.started) return
    this.runtime.poke()
  }

  /** Resolve once the runtime has drained all currently-claimable work to idle. */
  idle(): Promise<void> {
    return this.runtime.idle()
  }

  private ensureStarted(): void {
    if (this.started) return
    this.runtime.start()
    this.started = true
  }

  stop(): void {
    this.runtime.stop()
    this.started = false
  }
}

/**
 * RFC 0242: append a user prompt + a queued run to a branch-log WITHOUT a
 * runtime, so a REMOTE responder (a background sandbox running `mod session
 * serve`, or any other watcher) drains it. Standalone so a caller that only
 * hands off work — the web app's background mode — does not need to construct a
 * full RunSession (no inference/tool seams). Returns the run id and its projected
 * stream, which fills in as the remote responder writes to the synced log.
 *
 * This is the canonical "queue a run for someone else to run" primitive: the
 * exact message + run entries `RunSession.startRun` appends, minus the local
 * drive. Leave `responder` unset for an unaddressed run (any watcher claims), or
 * address it to a specific device (RFC 0240).
 */
/**
 * The three flat fields a selection is written as (RFC 0297 §4e).
 *
 * Exported because a captured session appends its message on a different path,
 * and a selection recorded on one and not the other would be a field that
 * appears only when the agent happens to be native.
 *
 * ALL THREE OR NONE. A label with no document names a passage in nothing, and a
 * reader would have no row to attach it to.
 */
export function selectionFields(
  selection: MessageSelection | null | undefined,
): Record<string, string> {
  if (!selection?.documentId || !selection.label) return {}
  return {
    selectionDocumentId: selection.documentId,
    selectionLabel: selection.label,
    ...(selection.ref ? { selectionRef: selection.ref } : {}),
  }
}

/** The pane manifest is one JSON scalar because the log codec deliberately
 * flattens unknown nested values. Empty views write no field. */
export function viewFields(
  view: MessageView | null | undefined,
): Record<string, string> {
  if (!view?.panes.length) return {}
  return { viewJson: JSON.stringify(view) }
}

export function enqueueRemoteRun(
  log: BranchLog,
  prompt: string,
  opts: {
    agent?: string
    responder?: ToolWorkerId | null
    /** Whiffletree `run.requestedModel` — the model the user addressed. The
     *  worker resolves against it and records the result as `resolvedModel`
     *  (RFC 0262). Absent → the worker picks its device/default. */
    requestedModel?: { provider: string; model: string } | null
    /** World skills this turn was asked for (RFC 0283 S5), by catalog id. */
    skills?: string[] | null
    /** RFC 0288: attachment pointers. They ride on the MESSAGE, not the run:
     *  they are part of what was said, not of how it is to be answered. */
    attachments?: AttachmentRef[] | null
    /** RFC 0304 3d: shell-captured images, hidden from transcript surfaces. */
    viewAttachments?: AttachmentRef[] | null
    /** RFC 0286 §5a: the session to notify when this dispatched turn ends. */
    dispatch?: RunDispatch | null
    /** RFC 0348 3d: the session whose `send_message` this is. */
    sentBy?: string | null
    /** RFC 0297 §4e: what the person was pointing at. Rides on the MESSAGE for
     *  the reason attachments do: it is part of what was said. */
    selection?: MessageSelection | null
    /** RFC 0304 3d: the screen at send time, carried on the message. */
    view?: MessageView | null
    /** RFC 0302 S3: branch frontier captured by the sending composer. */
    parents?: EntryId[] | null
    /** RFC 0302 S3: run this turn as a BRANCH, pinned to its own ancestry,
     *  rather than continuing from the frontier as it stands at claim time. */
    branch?: boolean
  } = {},
): StartedRun {
  // `branch` stays in runOpts: it is recorded on the RUN, while parents place
  // the MESSAGE. The two are a pair — a branch is only meaningful against the
  // heads its post was sent from — but they are written to different entries.
  const { attachments, viewAttachments, selection, view, parents, ...runOpts } =
    opts
  const msgId = log.append(
    {
      type: 'message',
      payload: {
        role: 'user',
        status: 'completed',
        text: prompt,
        toolUses: [],
        // Omitted when empty rather than written as [], so a message nobody
        // attached anything to stays byte-identical to one written before this
        // field existed.
        ...(attachments?.length ? { attachments } : {}),
        ...(viewAttachments?.length ? { viewAttachments } : {}),
        ...selectionFields(selection),
        ...viewFields(view),
      },
    },
    parents ?? log.heads(),
  )
  return queueRunForEntry(log, msgId, runOpts)
}

/**
 * Queue a run for an entry ALREADY on the log, with no message of its own.
 *
 * Split out of `enqueueRemoteRun` (which is this plus the user message) because
 * RFC 0286 §5a needs the run half without the message half: a dispatch reply is
 * an EVENT on the sender's log, and appending it as a user message would make it
 * indistinguishable from the person typing. The prompt entry is then the event,
 * so a reader of the run can see exactly what woke it.
 */
export function queueRunForEntry(
  log: BranchLog,
  promptEntryId: EntryId,
  opts: {
    agent?: string
    responder?: ToolWorkerId | null
    requestedModel?: { provider: string; model: string } | null
    skills?: string[] | null
    /** RFC 0286 §5a: the sender to notify when THIS turn ends. */
    dispatch?: RunDispatch | null
    /** RFC 0348 3d: the session whose `send_message` this is. */
    sentBy?: string | null
    /** RFC 0302 S3: pin this turn's context to its own ancestry. */
    branch?: boolean
    /** Stable only for a convergent system occurrence. */
    runId?: EntryId
  } = {},
): StartedRun {
  // Placement (RFC 0261 slice 3): select the device for the thread via
  // worker_migration (whiffletree `selected_tool_worker`) rather than a per-run
  // field. Parent the run to the migration so it is in the run's causal history.
  let parent = promptEntryId
  if (
    opts.responder &&
    log.activeToolWorker(promptEntryId) !== opts.responder
  ) {
    parent = log.migrateWorker(opts.responder, {
      fromWorkerId: log.activeToolWorker(promptEntryId) ?? undefined,
    })
  }
  const payload: NewEntry = {
    type: 'run',
    payload: {
      promptMessageId: promptEntryId,
      agent: opts.agent ?? 'default',
      status: 'queued',
      ...(opts.requestedModel ? { requestedModel: opts.requestedModel } : {}),
      // Omitted when empty rather than written as [], so a run nobody asked a
      // skill for is byte-identical to one written before this field existed.
      ...(opts.skills?.length ? { skills: opts.skills } : {}),
      // Same rule: a run nobody asked to hear about carries no dispatch at
      // all. Flattened, per RunData: nested objects do not survive the codec.
      ...(opts.dispatch
        ? {
            dispatchFrom: opts.dispatch.fromSessionId,
            dispatchId: opts.dispatch.dispatchId,
            // RFC 0348 3h: written only when the dispatcher narrowed it.
            ...(opts.dispatch.events?.length && opts.dispatch.events.length < 2
              ? { dispatchEvents: opts.dispatch.events.join(',') }
              : {}),
          }
        : {}),
      // Absent means a person, or an event, started this turn.
      ...(opts.sentBy ? { sentBy: opts.sentBy } : {}),
      // Same rule again: absent means continue, which is what every run
      // written before RFC 0302 S3 meant and still means.
      ...(opts.branch ? { branch: true } : {}),
    },
  }
  const runId = opts.runId
    ? log.appendWithId(opts.runId, payload, [parent])
    : log.append(payload, [parent])
  return { runId, stream: projectRunStream(log, runId) }
}
